import { useEffect } from 'react';

/**
 * Keeps `selectedIds` covering every row on the currently-visible pickups page
 * while "select all" is active, as the page data changes (poll / refetch /
 * pagination).
 *
 * Root cause of the pickups-page crash (React error #185, "Maximum update depth
 * exceeded"): this sync used to live INSIDE a `useMemo` that called
 * `setSelectedIds(...)` — i.e. setState during render. The pickup query returns
 * a fresh array reference on every poll, so the memo re-ran and set a NEW array
 * on every render → render → setState → an unbounded loop that the ErrorBoundary
 * caught and blanked the merchant's view (surfacing under the innermost child
 * SlotModal in the leaf-first component stack).
 *
 * The fix moves the sync into an effect AND emits a new selection ONLY when the
 * id CONTENT actually differs. Returning the previous array on an equal-content
 * update makes React bail out of the state change, so a fresh-but-equal
 * `pageKeys` reference each render can never spin the render loop back up —
 * defense-in-depth even if the caller hands us an unstable array.
 */
export const useSelectAllPageSync = (
  isAllSelected: boolean,
  pageKeys: string[],
  setSelectedIds: (updater: (prev: string[]) => string[]) => void,
): void => {
  useEffect(() => {
    if (!isAllSelected) return;
    setSelectedIds((prev) =>
      prev.length === pageKeys.length && prev.every((id, index) => id === pageKeys[index])
        ? prev
        : pageKeys,
    );
  }, [isAllSelected, pageKeys, setSelectedIds]);
};
